import { supabase } from './supabase';

/**
 * AI token auto top-up — client helpers.
 * Settings live on the business's ai_tokens row; writes go through
 * owner-checked RPCs and the charge itself runs in the
 * ai-tokens-auto-topup edge function.
 */

export interface AutoTopupSettings {
  enabled: boolean;
  threshold: number | null;
  target: number | null;
  monthlyLimit: number | null;
  periodPurchased: number;
  pausedReason: 'limit_reached' | 'payment_failed' | 'payment_failed_ack' | null;
  lastError: string | null;
  tokenBalance: number;
}

export async function fetchAutoTopupSettings(businessId: string): Promise<AutoTopupSettings | null> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('ai_tokens')
    .select(
      'token_balance, auto_topup_enabled, auto_topup_threshold, auto_topup_target, auto_topup_monthly_limit, auto_topup_period_purchased, auto_topup_paused_reason, auto_topup_last_error',
    )
    .eq('business_id', businessId)
    .maybeSingle();
  if (error || !data) return null;
  return {
    enabled: !!data.auto_topup_enabled,
    threshold: data.auto_topup_threshold != null ? Number(data.auto_topup_threshold) : null,
    target: data.auto_topup_target != null ? Number(data.auto_topup_target) : null,
    monthlyLimit: data.auto_topup_monthly_limit != null ? Number(data.auto_topup_monthly_limit) : null,
    periodPurchased: Number(data.auto_topup_period_purchased) || 0,
    pausedReason: (data.auto_topup_paused_reason as AutoTopupSettings['pausedReason']) ?? null,
    lastError: data.auto_topup_last_error ?? null,
    tokenBalance: Number(data.token_balance) || 0,
  };
}

export async function saveAutoTopupSettings(params: {
  businessId: string;
  enabled: boolean;
  threshold?: number | null;
  target?: number | null;
  monthlyLimit?: number | null;
}): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  const { error } = await supabase.rpc('set_ai_token_auto_topup', {
    p_business_id: params.businessId,
    p_enabled: params.enabled,
    p_threshold: params.threshold ?? null,
    p_target: params.target ?? null,
    p_monthly_limit: params.monthlyLimit ?? null,
  });
  if (error) throw new Error(error.message);
}

export async function resolveAutoTopupPause(
  businessId: string,
  mode: 'approve_more' | 'retry_clear' | 'keep_paused',
): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  const { error } = await supabase.rpc('resolve_ai_token_auto_topup_pause', {
    p_business_id: businessId,
    p_mode: mode,
  });
  if (error) throw new Error(error.message);
}

export interface AutoTopupRunResult {
  toppedUp?: boolean;
  tokensAdded?: number;
  newBalance?: number;
  paymentFailed?: boolean;
  pausedReason?: string;
  skipped?: string;
  error?: string;
}

/** Fire the server-side check (charges the card on file when due). */
export async function runAutoTopupCheck(businessId: string): Promise<AutoTopupRunResult> {
  if (!supabase) return { error: 'Supabase client not initialized' };
  try {
    const { data, error } = await supabase.functions.invoke('ai-tokens-auto-topup', {
      body: { businessId },
    });
    if (error) return { error: error.message };
    return (data || {}) as AutoTopupRunResult;
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Auto top-up check failed' };
  }
}
